import { Injectable } from '@angular/core';
import { WebApiService } from 'starbase-login';
import { Observable, Subject, of } from 'rxjs';
import { catchError, retry, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { ImageFile } from '../models/imageFile';
import { HttpClient } from '@angular/common/http';
import { ImageType } from '../models/imageType';
import { RelatedImageSetViewDataModel } from '../models/relatedImageSetViewDataModel';
import { FilterParams, StarbaseDataServiceWithRequisites } from 'starbase-launcher';
import { environment } from '../../environments/environment';
import { RsSequenceUpdate } from '../models/rsSequenceUpdate';
import { BatchEdit } from '../models/batchEdit';
import { RelatedSetLinkingModel } from '../models/relatedSetLinkingModel';

const IMAGE_MAX_FILESIZE = 155 * (1024 * 1024); // 31457280; // 30MB // 157286400; // 150MB (testing)

// Return value fron /api/ImageModels/GetRelatedImageSetByFilterParams
export type RelatedImageSetBroadSearchResults = {
  relatedImages: RelatedImageSetViewDataModel[],
  relatedImageModels: ImageFile[]
};

@Injectable({
  providedIn: 'root'
})
export class ImageService extends StarbaseDataServiceWithRequisites<ImageFile> {

  public getOneForCrawler = '/api/ImageModels/DataForCrawler/';
  public broadSearchRSUri = '/api/ImageModels/GetRelatedImageSetByFilterParams'; // GET all RS by filterParams 
  protected _staleDataTimeout = 3500;

  protected _getAllUri = '/api/ImageModels'; // GET all
  protected _getOneUri = '/api/ImageModels/'; // GET 1
  protected _broadSearchUri = '/api/ImageModels/GetImagesByFilterParams'; // GET all by filterParams
  protected _postUri = '/api/ImageModels'; // POST 1
  protected _putUri = '/api/ImageModels/'; // PUT 1
  protected _deleteUri = '/api/ImageModels/'; // DELETE 1
  protected _regenerateThumbnailUri = this._putUri + 'RegenerateThumbnailImage/';
  protected _getCivilianRecordsInfo = this._getOneUri + 'GetCivilianResearchIds';
  protected _getOtherlibCountsUri = this._getOneUri + 'GetOtherLibCounts';
  protected _getAllImageTypeCountUri = '/api/ImageModels/GetAllImageTypeCounts';
  protected _updateRSSeqNoUri = '/api/ImageModels/UpdateRSSequenceNumbers';

  // private imagesByFiltersUrl = '/api/ImageModels/GetImagesByFilters'; // GET dataset with chosen filters applied
  private imageByTypeUrl = '/api/ImageModels/GetImagesByType/'; // GET all by type string qry param
  private imageLocationsUrl = '/api/ImageModels/GetImageLocations'; // GET distinct locations
  private imageFilterOptionsUrl = '/api/ImageModels/GetImageFilterOptions'; // GET all filter options
  private batchEditImageRecordsUrl = '/api/ImageModels/BatchEdit'; // edit many (via POST call)
  private relatedImageSetDataUrl = '/api/ImageModels/GetRelatedImageSetData/'; // GET all related image set data from the view.
  // eslint-disable-next-line max-len
  private relatedImageSetImagesByFilterParams = '/api/ImageModels/GetRSImagesByFilterParams'; // GET all related image set images by filter params.
  private naiveFilterCache: object = null;
  private ledgerNavDataUrl = '/api/ImageModels/GetLedgerViewNavigationData/';
  private navigateLedgerSequenceUrl = '/api/ImageModels/NavigateLedgerSequence/';

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private UPLOADER_SERVER_ADDRESS = environment.testMode
    ? environment.TESTING_UPLOADER_VM_API_SERVER_ADDRESS
    : environment.PROD_UPLOADER_VM_API_SERVER_ADDRESS;

  private _thumbnailSelectedTrigger$: Subject<number>;
  // eslint-disable-next-line brace-style
  public get thumbnailSelectedTrigger$(): Subject<number> { return this._thumbnailSelectedTrigger$; }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  constructor(private _http: HttpClient, private _webAPI: WebApiService) {
    super(_http, _webAPI);
    this._thumbnailSelectedTrigger$ = new Subject<number>();
  }

  public get imageMaxSize() {
    return IMAGE_MAX_FILESIZE;
   }

  public thumbnailSelected(id: number) {
    this._thumbnailSelectedTrigger$.next(id);
  }

  getRelatedSetViewDataByFilterParamsBroadSearch(filterParams: FilterParams<ImageFile>) {
    const fp: FilterParams<ImageFile> = JSON.parse(JSON.stringify(filterParams));
    fp.collection = [];
    this.dataFilterParamsCancel$.next();

    return this._http.post<RelatedImageSetBroadSearchResults>(
      this.server + this.broadSearchRSUri,
      fp,
      { headers: this._webAPI.getApiHeaders('post') } 
    ).pipe(
        retry(this.defaultRetries),
        catchError(e => this._webAPI.handleRequestError(e)),
        takeUntil(this.dataFilterParamsCancel$),
        shareReplay({bufferSize: 1, refCount: true})
    );
  }

  getAllImagesByTypeFromServer(imgType: ImageType): Observable<ImageFile[]> {
    return this.getAllData(this.imageByTypeUrl + imgType.uid);
  }

  getImageLocations(): Observable<string[]> {
    return this.getAllRequisiteData<string>(this.imageLocationsUrl);
  }
  getImageFilterOptions(): Observable<object> {
    if (this.naiveFilterCache) {
      // eslint-disable-next-line no-console
      console.info('Returning cached filter objects.');
      return of(this.naiveFilterCache);
    } else {
      return this.getRequisiteData<object>(this.imageFilterOptionsUrl).pipe(tap(x => this.naiveFilterCache = x));
    }
  }
  public fillFilterOptionsCache(): void {
    this.getImageFilterOptions().subscribe();
  }
  public clearFilterCache(): void {
    this.naiveFilterCache = null;
  }

  // can pass NULL to the sourceId param.
  postImageToServer(img: File,
    typeOfImageId: number,
    batchNo: number,
    sourceCallId: number,
    sourceId: number,
    cancellationSubject: Subject<void>): Observable<ImageFile> { // no retries
     const formData: FormData = new FormData();
     formData.append('imageFile', img, img.name);
     formData.append('imageTypeId', typeOfImageId.toString());
     formData.append('batchNo', batchNo.toString());
     formData.append('sourceCallNo', sourceCallId.toString());
     formData.append('sourceId', (sourceId ? sourceId.toString() : '-1'));
     // console.debug(this.UPLOADER_SERVER_ADDRESS);
     return this._http.post<ImageFile>(this.UPLOADER_SERVER_ADDRESS + this.postUri, formData,
       { headers: this._webAPI.getApiHeaders('test') }).pipe(
         takeUntil(cancellationSubject),
         catchError(e => this._webAPI.handleRequestError(e))
       );
  }
  updateImageDataOnServer(imgData: ImageFile, actionTaken: string = null): Observable<ImageFile> {
    imgData.numericSortString = null;
    return this.putData(this.putUri, imgData.uid + ((actionTaken) ? '?actionTaken=' + actionTaken : ''), imgData);
  }

  regenerateImageThumbnail(imgData: ImageFile): Observable<ImageFile> {
    return this.putData(this._regenerateThumbnailUri, imgData.uid, null);
  }

  getAllRelatedImageSetData(id: number): Observable<RelatedImageSetViewDataModel[]> {
    if (id === undefined || id === null) {
      console.warn('getAllRelatedImageSetData called with invalid id');
      return of([]);
    }
    
    return this._http.get<RelatedImageSetViewDataModel[]>(
      this.server + this.relatedImageSetDataUrl + id, 
      {
        headers: this._webAPI.getApiHeaders('get')
      }
    ).pipe(
      tap(data => console.log('Related image set data received:', data)),
      catchError(error => {
        console.error('Error fetching related image set data:', error);
        return of([]);
      })
    );
  }

  getLedgerNavigationData(relatedSetUID: number, currentImageUID: number, windowSize: number): Observable<any> {
    const params = `${relatedSetUID}?currentImageUID=${currentImageUID}&windowSize=${windowSize}`;
    return this.getRequisiteData(this.ledgerNavDataUrl + params);
  }

  navigateLedgerSequence(relatedSetUID: number, direction: 'next' | 'previous', currentSequence: number): Observable<any> {
    const params = `${relatedSetUID}/${direction}?currentSequence=${currentSequence}`;
    return this.getRequisiteData(this.navigateLedgerSequenceUrl + params);
  }

  getAllRSImagesByFilterParams(filterParams: FilterParams<ImageFile>): Observable<FilterParams<ImageFile>> {
    const fp: FilterParams<ImageFile> = JSON.parse(JSON.stringify(filterParams)); // copy
    fp.collection = [];
    this.dataFilterParamsCancel$.next();
    return this._http.post<FilterParams<ImageFile>>(
      this.server + this.relatedImageSetImagesByFilterParams,
      fp,
      { headers: this._webAPI.getApiHeaders('post') })
      .pipe(
        retry(this.defaultRetries),
        catchError(e => this._webAPI.handleRequestError(e)),
        takeUntil(this.dataFilterParamsCancel$),
        shareReplay({bufferSize: 1, refCount: true})
      );
  }

  getAllImageTypeCount(uidArray: any, roleBoolean: boolean){
    return this.postRequisiteData(this._getAllImageTypeCountUri + '?roleBool=' + roleBoolean, uidArray);
  }

  updateRelatedSetSequenceNumber(uidSeqPairs: any, rsTuple: [string, number]) {
    let toPassArray = new RsSequenceUpdate();
    toPassArray.imageUIDs = uidSeqPairs;
    toPassArray.relatedSetName = rsTuple[0];
    toPassArray.relatedSetUID = rsTuple[1];
    return this.postRequisiteData(this._updateRSSeqNoUri, toPassArray);
  }
   
  createRelatedSetLink(linkModel: RelatedSetLinkingModel): Observable<RelatedSetLinkingModel> {
    return this.postRequisiteData<RelatedSetLinkingModel>(
      '/api/RelatedImageSetModels/Link', 
      linkModel
    );
  }

  civilianRecordsInfo(trueOrFalse: boolean): Observable<any>{
    return this.getRequisiteData(this._getCivilianRecordsInfo + '?trueOrFalse=' + trueOrFalse);
  }

  getOtherLibCounts(filters: FilterParams<ImageFile>, civilianMode: boolean) {
    return this.postRequisiteData(this._getOtherlibCountsUri + '?civilianMode=' + civilianMode, filters);
  }

  batchEditImageRecordsOnServer(edit: BatchEdit): Observable<any> {
     return this._http.post(
      this.server + this.batchEditImageRecordsUrl,
      edit,
      { headers: this._webAPI.getApiHeaders('post') })
       .pipe(
         catchError(e => this._webAPI.handleRequestError(e)));
  }
}
